import type { IWorkspaceDB } from '../../db/interface';
import type {
    ILanguageDto,
    IMediumDto,
    IModelSource,
    ISiteObjectDto,
    ISiteObjectTranslationDto,
    ITourDto,
} from '../../model/interface';
import mediaUtil from './media.util';

async function mediaForSiteObject(
    db: IWorkspaceDB,
    siteObject: ISiteObjectDto,
    language: ILanguageDto,
): Promise<IMediumDto[]> {
    const shouldOpenClose = !db.isOpen();

    if (shouldOpenClose) {
        await db.open();
    }

    const allTranslations = await siteObject.translations(db as IModelSource);
    /* If only 1 translation provided assume universal(no-translation-needed) & include */
    const matchTranslations =
        allTranslations.length > 1
            ? allTranslations.filter(sot => sot.languageId === language.id)
            : allTranslations;

    const downloadList = matchTranslations
        .map(sot => sot.mediumId)
        .filter(mediumId => mediumId && mediumId.length > 1);

    const media = await Promise.all(downloadList.map(mediumId => db.get('IMediumDto', mediumId)));

    if (siteObject.styleId) {
        const style = await db.get('IStyleDto', siteObject.styleId);
        media.push(...(await mediaUtil.mediaForStyling(db, style)));
    }

    const localMediaIds = [
        siteObject.foregroundImageMediumId,
        siteObject.backgroundImageMediumId,
        siteObject.menuImageMediumId,
    ];

    const allLocalMedia = await Promise.all(
        localMediaIds.filter(id => id).map(id => db.get('IMediumDto', id)),
    );

    media.push(...allLocalMedia);

    if (shouldOpenClose) {
        db.close();
    }

    return media;
}

async function mediaUsedForSiteObjectTranslationsInLanguage(
    db: IWorkspaceDB,
    allSiteObjectTranslations: ISiteObjectTranslationDto[],
    language: ILanguageDto,
): Promise<IMediumDto[]> {
    const shouldOpenClose = !db.isOpen();

    if (shouldOpenClose) {
        await db.open();
    }

    const allMediaWithTranslations = allSiteObjectTranslations.filter(
        sot => sot.mediumId,
    ); /* If the mediumId is blank, ignore */
    const matchMediaTranslations = await Promise.all(
        allMediaWithTranslations
            .filter(sot => sot.languageId === language.id)
            .map(sot => db.get('IMediumDto', sot.mediumId)),
    ); /* Filter all SOTs with valid mediumId, where language matches & pull sot.mediumId from db */

    /* Any media without an associated translation, treat as if it is universal (needed on all languages) & download regardless */
    const allMedia = await db.all('IMediumDto');
    const allMediaWithTranslationsIds = allMediaWithTranslations.map(medium => medium.mediumId);
    const mediaUniversal = allMedia.filter(
        medium => !allMediaWithTranslationsIds.includes(medium.id),
    );

    if (shouldOpenClose) {
        db.close();
    }

    /* Generate Mapping, so as to remove any duplicates */
    const downloadMedia: Record<string, IMediumDto> = {};

    mediaUniversal.forEach(medium => (downloadMedia[medium.id] = medium));
    matchMediaTranslations.forEach(medium => (downloadMedia[medium.id] = medium));

    const downloadList = Object.values(downloadMedia);

    return downloadList;
}

async function mediaUsedForLanguage(
    db: IWorkspaceDB,
    language: ILanguageDto,
): Promise<IMediumDto[]> {
    const shouldOpenClose = !db.isOpen();

    if (shouldOpenClose) {
        await db.open();
    }

    /* Get a list of all media associated with SiteObjectTranslations & filter these by #language */
    const allSiteObjectTranslations = await db.all('ISiteObjectTranslationDto');
    const downloadList = mediaUsedForSiteObjectTranslationsInLanguage(
        db,
        allSiteObjectTranslations,
        language,
    );

    if (shouldOpenClose) {
        db.close();
    }

    return downloadList;
}

async function _siteObjectsForTour(db: IWorkspaceDB, tour: ITourDto): Promise<ISiteObjectDto[]> {
    const shouldOpenClose = !db.isOpen();

    if (shouldOpenClose) {
        await db.open();
    }

    const tourRelatedSiteObjects = await db.all('ITourRelatedSiteObjectDto');
    const siteObjectIds = tourRelatedSiteObjects
        .filter(x => x.tourId === tour.id)
        .map(x => x.siteObjectId);
    const siteObjects = await Promise.all(siteObjectIds.map(id => db.get('ISiteObjectDto', id)));

    if (shouldOpenClose) {
        db.close();
    }

    return siteObjects;
}

async function mediaForLanguageWithTour(
    db: IWorkspaceDB,
    language: ILanguageDto,
    tour: ITourDto,
): Promise<IMediumDto[]> {
    const shouldOpenClose = !db.isOpen();

    if (shouldOpenClose) {
        await db.open();
    }

    const mediaList: IMediumDto[] = [];

    if (tour.styleId) {
        const tourStyle = await tour.style(db as IModelSource);
        mediaList.push(...(await mediaUtil.mediaForStyling(db, tourStyle)));
    }

    const siteObjects = await _siteObjectsForTour(db, tour);

    const soMediaList = await Promise.all(
        siteObjects.map(so => mediaForSiteObject(db, so, language)),
    );

    soMediaList.forEach(soMedia => mediaList.push(...soMedia));

    if (shouldOpenClose) {
        db.close();
    }

    return mediaList;
}

export default Object.freeze({
    mediaUsedForLanguage,
    mediaForSiteObject,
    mediaForLanguageWithTour,
});
